import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';

const TeamContext = createContext();

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

export const TeamProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const functions = getFunctions(app, 'us-central1');

  // Fetch user's teams
  const fetchTeams = async () => {
    if (!currentUser) {
      setTeams([]);
      setSelectedTeam(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const getMyTeams = httpsCallable(functions, 'teams-getMyTeams');
      const result = await getMyTeams();

      if (result.data.success) {
        setTeams(result.data.teams || []);
        
        // Auto-select first team if none selected
        if (!selectedTeam && result.data.teams.length > 0) {
          setSelectedTeam(result.data.teams[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Create a new team
  const createTeam = async (name) => {
    try {
      const createTeamFn = httpsCallable(functions, 'teams-createTeam');
      const result = await createTeamFn({ name });

      if (result.data.success) {
        await fetchTeams();
        return result.data.team;
      }
      throw new Error('Failed to create team');
    } catch (err) {
      console.error('Error creating team:', err);
      throw err;
    }
  };

  // Generate invite link
  const generateInviteLink = async (teamId, options = {}) => {
    try {
      const generateInviteLinkFn = httpsCallable(functions, 'teams-generateInviteLink');
      const result = await generateInviteLinkFn({ teamId, ...options });

      if (result.data.success) {
        return {
          inviteId: result.data.inviteId,
          inviteUrl: result.data.inviteUrl,
        };
      }
      throw new Error('Failed to generate invite link');
    } catch (err) {
      console.error('Error generating invite link:', err);
      throw err;
    }
  };

  // Decline team invite
  const declineInvite = async (inviteId) => {
    try {
      const declineInviteFn = httpsCallable(functions, 'teams-declineInvite');
      const result = await declineInviteFn({ inviteId });

      if (result.data.success) {
        return true;
      }
      throw new Error('Failed to decline invite');
    } catch (err) {
      console.error('Error declining invite:', err);
      throw err;
    }
  };

  // Join team by invite
  const joinTeamByInvite = async (inviteId) => {
    try {
      const joinTeamFn = httpsCallable(functions, 'teams-joinTeamByInvite');
      const result = await joinTeamFn({ inviteId });

      if (result.data.success) {
        await fetchTeams();
        return result.data.teamId;
      }
      throw new Error('Failed to join team');
    } catch (err) {
      console.error('Error joining team:', err);
      throw err;
    }
  };

  // Get team members
  const getTeamMembers = async (teamId) => {
    try {
      const getTeamMembersFn = httpsCallable(functions, 'teams-getTeamMembers');
      const result = await getTeamMembersFn({ teamId });

      if (result.data.success) {
        return result.data.members || [];
      }
      throw new Error('Failed to get team members');
    } catch (err) {
      console.error('Error getting team members:', err);
      throw err;
    }
  };

  // Add team member
  const addTeamMember = async (teamId, email, name, role) => {
    try {
      const addTeamMemberFn = httpsCallable(functions, 'teams-addTeamMember');
      const result = await addTeamMemberFn({ teamId, email, name, role });

      if (result.data.success) {
        return result.data.member;
      }
      throw new Error('Failed to add team member');
    } catch (err) {
      console.error('Error adding team member:', err);
      throw err;
    }
  };

  // Remove team member
  const removeTeamMember = async (teamId, memberId) => {
    try {
      const removeTeamMemberFn = httpsCallable(functions, 'teams-removeTeamMember');
      const result = await removeTeamMemberFn({ teamId, memberId });

      if (result.data.success) {
        return true;
      }
      throw new Error('Failed to remove team member');
    } catch (err) {
      console.error('Error removing team member:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchTeams();
  }, [currentUser]);

  const value = {
    teams,
    selectedTeam,
    setSelectedTeam,
    loading,
    error,
    fetchTeams,
    createTeam,
    declineInvite,
    removeTeamMember,
    generateInviteLink,
    joinTeamByInvite,
    getTeamMembers,
    addTeamMember,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};

